<script setup lang="ts">
import { computed } from 'vue'

import { getFacebookEmbedUrl, getYoutubeEmbedUrl, resolveVideoType } from './resolveVideo'
import type { PreviewVideoProps } from './types'

import { Icon } from '@/shared/ui/icon'
import { FloatingLayer, OverlayPortal } from '@/shared/ui/overlay'

// Fullscreen video lightbox. PORT-AND-SHARPEN of OrphexPreviewVideo.vue — same shape as
// PreviewImage. Dispatches by URL shape (youtube / facebook / direct / image fallback) via the
// pure `resolveVideo` helpers. Sharpened: dropped `z-2000` for the central FloatingLayer stack,
// dropped the `o-data-btn` dependency for a plain button + inline SVG, close label is a caller prop. A direct
// video renders with `controls`, so if the browser blocks `autoplay` the user still has playback
// controls (contract 02 §14 — "video autoplay blocked → controls fallback").
const props = withDefaults(defineProps<PreviewVideoProps>(), {
  src: '',
  thumbnail: '',
  alt: '',
  closeLabel: 'ui.media.close',
})

const open = defineModel<boolean>({ default: false })

const videoType = computed(() => resolveVideoType(props.src))
const youtubeEmbedUrl = computed(() => getYoutubeEmbedUrl(props.src))
const facebookEmbedUrl = computed(() => getFacebookEmbedUrl(props.src))

function close(): void {
  open.value = false
}
</script>

<template>
  <OverlayPortal>
    <FloatingLayer
      v-if="open"
      kind="media"
      class="ui-lightbox fixed inset-0 flex items-center justify-center p-10 overflow-auto bg-media-backdrop"
      @click="close"
    >
      <div class="ui-lightbox__toolbar fixed top-6 end-6 flex gap-3" @click.stop>
        <button
          type="button"
          class="ui-lightbox__btn inline-flex items-center justify-center w-12 h-12 rounded-full text-on-media bg-media-control cursor-pointer transition-[background-color] duration-150 ease-[ease] hover:bg-media-control-hover"
          :aria-label="closeLabel"
          @click="close"
        >
          <Icon name="x" :size="20" />
        </button>
      </div>
      <div
        class="ui-lightbox__stage ui-lightbox__stage--video flex h-5/6 max-h-[90vh] w-full max-w-[min(60rem,90vw)] items-center justify-center"
        @click.stop
      >
        <iframe
          v-if="videoType === 'youtube'"
          class="ui-lightbox__frame h-full max-h-[90vh] min-h-[22.5rem] w-full min-w-0 border-0 bg-media-canvas"
          :src="youtubeEmbedUrl"
          allow="
            accelerometer;
            autoplay;
            clipboard-write;
            encrypted-media;
            gyroscope;
            picture-in-picture;
            web-share;
          "
          allowfullscreen
          referrerpolicy="strict-origin-when-cross-origin"
        />
        <iframe
          v-else-if="videoType === 'facebook'"
          class="ui-lightbox__frame h-full max-h-[90vh] min-h-[22.5rem] w-full min-w-0 border-0 bg-media-canvas"
          :src="facebookEmbedUrl"
          allow="autoplay; encrypted-media"
          allowfullscreen
        />
        <video
          v-else-if="videoType === 'direct'"
          class="ui-lightbox__frame h-full max-h-[90vh] min-h-[22.5rem] w-full min-w-0 border-0 bg-media-canvas"
          :src="src"
          controls
          autoplay
          playsinline
        />
        <img
          v-else
          class="ui-lightbox__img max-w-full max-h-full object-contain transition-[transform] duration-150 ease-[ease]"
          :src="thumbnail || src"
          :alt="alt"
        />
      </div>
    </FloatingLayer>
  </OverlayPortal>
</template>
